import { create } from "zustand";
import axios from "axios";
/*import { AccessToken } from "@okta/okta-auth-js";*/
import IReturnResults from "common/interfaces/IReturnResults";
import { getAxiosErrorReturnResults } from "common/utils/errorHandling";
import { getNetwork } from "common/utils/networkExtensions";

export enum DistanceUnit {
  Feet = 1,
  Meters = 2,
}

export interface IDeviceInstance {
  deviceInstanceID: number;
  deviceID: number;
  deviceTag: string;
  xPosition: number;
  yPosition: number;
  zPosition: number;
  range: number;
  height: number;
  updateRateID: number | null;
  note: string | null;
  updatedDate: Date | null;
  image: string;
  initial: boolean;
  rowVersion: string | null;
}

interface INetworkMetadata {
  usagePercent: number;
  isUsagePercentDirty: boolean;
  isVisible: boolean;
}

interface INetworkContainerMetadata {
  isVisible: boolean;
}

export interface INetwork {
  networkID: number;
  deviceInstances: IDeviceInstance[];
  updatedDate: Date;
  rowVersion: string | null;
  networkContainerID: number;
  metaData: INetworkMetadata;
}

export interface INetworkContainer {
  networkContainerID: number;
  networkContainerName: string;
  gatewayDeviceInstance: IDeviceInstance | null;
  updatedDate: Date;
  rowVersion: string | null;
  networks: INetwork[];
  metaData: INetworkContainerMetadata;
}

export interface IUpdateNetworkContainer {
  networkContainerID: number | undefined;
  networkContainerName: string | undefined;
  gatewayDeviceInstance: IDeviceInstance | null | undefined;
  rowVersion: string | null | undefined;
  deleted: boolean | undefined;
}

export interface IProject {
  projectID: number;
  name: string;
  plotPlanImage: string;
  imageFileType: string;
  scalingFactor: number | null;
  distanceID: number;
  version: number;
  updatedDate: Date;
  networkContainers: INetworkContainer[];
  rowVersion: string;
  isOwned: boolean;
}

export interface IObstructionInstance {
  obstructionInstanceID: number;
  obstructionTypeID: number;
  projectID: number;
  obstructionName: string;
  xPosition: number;
  yPosition: number;
  zPosition: number;
  height: number | null;
  width: number | null;
  length: number | null;
  updatedDate: Date | null;
  initial: boolean;
  rowVersion: string | null;
}

export interface IUpdateObstructionInstance {
  obstructionInstanceID: number | undefined;
  obstructionTypeID: number | undefined;
  projectID: number | undefined;
  obstructionName: string | undefined;
  xPosition: number | undefined;
  yPosition: number | undefined;
  zPosition: number | undefined;
  height: number | null | undefined;
  width: number | null | undefined;
  length: number | null | undefined;
  updatedDate: Date | null | undefined;
  rowVersion: string | null | undefined;
  deleted: boolean | undefined;
}

export interface INewProject {
  projectName: string;
  projectImage: string;
  contentType: string;
}

interface IUpdateDevice {
  DeviceInstanceID: number;
  NetworkID: number;
  DeviceTag: string | null;
  XPosition: number | null;
  YPosition: number | null;
  Height: number | null;
  Range: number | null;
  UpdateRateID: number | null;
  Note: string | null;
  RowVersion: string;
}
interface IAddDevice {
  DeviceInstanceID: number;
  NetworkID: number;
  DeviceID: number;
  DeviceTag: string | null;
  XPosition: number;
  YPosition: number;
  Height: number | null;
  Range: number | null;
  UpdateRateID: number | null;
  Note: string | null;
}
interface IDeleteDevice {
  DeviceInstanceID: number;
  NetworkID: number;
  RowVersion: string;
}
interface IUpdateNetwork {
  NetworkID: number;
  RowVersion: string;
}
interface IAddNetwork {
  NetworkID: number;
  NetworkContainerID: number;
  AddDeviceInstanceReqs: IAddDevice[] | null;
}
interface IDeleteNetwork {
  NetworkID: number;
  RowVersion: string;
}
interface ISaveProject {
  ProjectID: number | null;
  RowVersion: string | null;
  ScalingFactor: number | null;
  DistanceID: number | null;
  AddNetworkReqs: IAddNetwork[] | null;
  UpdateNetworkReqs: IUpdateNetwork[] | null;
  DeleteNetworkReqs: IDeleteNetwork[] | null;
  AddDeviceInstanceReqs: IAddDevice[] | null;
  UpdateDeviceInstanceReqs: IUpdateDevice[] | null;
  DeleteDeviceInstanceReqs: IDeleteDevice[] | null;
  UpdateObstructionInstanceReqs: IUpdateObstructionInstance[] | null;
  UpdateNetworkContainerReqs: IUpdateNetworkContainer[] | null;
}

interface IModifiedNetworkContainer {
  networkContainerID: number;
  rowVersion: string | null;
  action: "update" | "delete";
}

interface IModifiedObstructionInstance {
  obstructionInstanceID: number;
  rowVersion: string | null;
  action: "update" | "delete";
}

export type ProjectStore = {
  fetchProject: (
    apiBaseUrl: string,
        getAccessToken: () => Promise<string | null>,
    id: number,
  ) => Promise<IReturnResults>;
  createProject: (
    apiBaseUrl: string,
      getAccessToken: () => Promise<string | null>,
    project: INewProject,
  ) => Promise<IReturnResults>;
    saveProject: (apiBaseUrl: string, getAccessToken: () => Promise<string | null>) => Promise<IReturnResults>;
  isDirty: boolean;
  projectID: number | null;
  setProjectID: (n: number | null) => void;
  projectName: string | null;
  isOwned: boolean;
  setName: (name: string) => void;
  rowVersion: string | null;
  plotPlanImage: string | null;
  setPlotPlanImage: (image: string, filetype: string) => void;
  imageFileType: string | null;
  scalingFactor: number | null;
  setScalingFactor: (factor: number) => void;
  distanceID: number;
  setDistanceID: (distanceID: number) => void;
  version: number | null;
  updatedDate: Date | null;
  networkContainers: INetworkContainer[];
  nextNewNetworkID: number;
  addNetwork: (networkContainerID: number) => number;
  deleteNetwork: (networkID: number) => void;
  activeNetworkID: number;
  activeNetwork: INetwork | null;
  setActiveNetwork: (networkID: number) => void;
  nextNewDeviceInstanceID: number;
  activeNetworkContainerID: number;
  activeNetworkContainer: INetworkContainer | null;
  setActiveNetworkContainer: (networkContainerID: number, setDefaultNetwork: boolean) => void;
  addNetworkContainer: (name: string) => number;
  updateNetworkContainer: (networkContainerID: number, newName: string) => void;
  deleteNetworkContainer: (networkContainerID: number) => void;
  nextNewNetworkContainerID: number;
  modifiedNetworkContainers: IModifiedNetworkContainer[];
  setGateway: (newGateway: IDeviceInstance) => number;
  updateGateway: (networkContainerID: number, gatewayInstance: IDeviceInstance) => void;
  activeGateway: IDeviceInstance | null;
  addDeviceInstance: (newDevice: IDeviceInstance, networkID: number) => number;
  updateDeviceInstances: (networkID: number, updateDevice: IDeviceInstance) => void;
  deleteDeviceInstance: (instanceID: number, networkID: number) => void;
  recalculateNetworkUsage: (apiBaseUrl: string, networkID: number) => Promise<IReturnResults>;
  obstructionInstances: IObstructionInstance[];
  addObstructionInstance: (newInstance: IObstructionInstance) => void;
  updateObstructionInstance: (obstructionID: number, updateInstance: IObstructionInstance) => void;
  deleteObstructionInstance: (obstructionID: number) => void;
  nextNewObstructionInstanceID: number;
  modifiedObstructionInstances: IModifiedObstructionInstance[];
  // not contained in database
  projectChanges: ISaveProject;
  loading: boolean;
  zoomPercent: number;
  setZoomPercent: (percent: number) => void;
  transparencyPercent: number;
  setTransparencyPercent: (percent: number) => void;
  iconPercent: number;
  setIconPercent: (percent: number) => void;
  enableStressTest: number;
  setEnableStressTest: (value: number) => void;
  setNetworkVisiblity: (networkID: number, isVisible: boolean) => void;
  resetProject: () => void;
};

const createCleanProjectChanges = (): ISaveProject => ({
  ProjectID: null,
  RowVersion: null,
  ScalingFactor: null,
  DistanceID: null,
  AddNetworkReqs: null,
  UpdateNetworkReqs: null,
  DeleteNetworkReqs: null,
  AddDeviceInstanceReqs: null,
  UpdateDeviceInstanceReqs: null,
  DeleteDeviceInstanceReqs: null,
  UpdateObstructionInstanceReqs: null,
  UpdateNetworkContainerReqs: null,
});

const createDeletedObstructionInstance = (
  obstructionInstanceID: number,
  rowVersion: string,
): IUpdateObstructionInstance => ({
  obstructionInstanceID: obstructionInstanceID,
  obstructionTypeID: undefined,
  projectID: undefined,
  obstructionName: undefined,
  xPosition: undefined,
  yPosition: undefined,
  zPosition: undefined,
  height: undefined,
  width: undefined,
  length: undefined,
  updatedDate: undefined,
  rowVersion: rowVersion,
  deleted: true,
});

const createDeletedNetworkContainer = (networkContainerID: number, rowVersion: string): IUpdateNetworkContainer => ({
  networkContainerID: networkContainerID,
  networkContainerName: undefined,
  gatewayDeviceInstance: undefined,
  rowVersion: rowVersion,
  deleted: true,
});

const useProjectStore = create<ProjectStore>((set, get) => ({
  isDirty: false,
  projectID: null,
  projectName: null,
  isOwned: true,
  rowVersion: null,
  plotPlanImage: null,
  imageFileType: null,
  scalingFactor: null,
  distanceID: DistanceUnit.Feet,
  version: null,
  updatedDate: null,
  networks: [],
  networkContainers: [],
  activeNetworkID: 0,
  activeNetwork: null,
  activeNetworkContainerID: 0,
  activeNetworkContainer: null,
  activeGateway: null,
  nextNewNetworkID: -1,
  nextNewNetworkContainerID: -1,
  modifiedNetworkContainers: [],
  nextNewDeviceInstanceID: -1,
  nextNewObstructionInstanceID: -1,
  obstructionInstances: [],
  modifiedObstructionInstances: [],

  fetchProject: async (apiBaseUrl, getAccessToken, id) => {
    try {
      set(() => ({ loading: true }));
      const accessToken = await getAccessToken();
      if (accessToken === null) {
        set((state) => ({ loading: true }));
        return { succeeded: false, errorMessage: "Unable to retrieve authorized access token." };
      }

      const response = await axios.get<{ project: IProject }>(`${apiBaseUrl}/api/project/project?id=${id}`, {
           headers: { Authorization: `Bearer ${accessToken}` },
      });
      console.log("fetchProject response", response.data);

      set((state) => ({
        ...response.data.project,
        networkContainers:
          response.data.project.networkContainers.length > 0
            ? response.data.project.networkContainers.map((networkContainer: INetworkContainer) => {
                return {
                  ...networkContainer,
                  metaData: {
                    isVisible: false,
                  },
                  networks:
                    networkContainer.networks.length > 0
                      ? networkContainer.networks.map((network: INetwork) => {
                          return {
                            ...network,
                            deviceInstances:
                              network.deviceInstances?.length > 0
                                ? network.deviceInstances.map((deviceInstance: IDeviceInstance) => {
                                    return {
                                      ...deviceInstance,
                                      image: "", //get from DeviceStore
                                      initial: false,
                                    };
                                  })
                                : [],
                            metaData: {
                              usagePercent: 0,
                              isUsagePercentDirty: true,
                              isVisible: false,
                            },
                          };
                        })
                      : [],
                };
              })
            : [],
        isDirty: false,
        loading: false,
        projectChanges: createCleanProjectChanges(),
      }));
      console.log("fetchedProject", get());
      if (get().networkContainers.length > 0) {
        get().setActiveNetworkContainer(get().networkContainers[0].networkContainerID, true);
      }
      return { succeeded: true, errorMessage: null };
    } catch (err) {
      set(() => ({ loading: false }));
      console.log("fetchProject threw an error", err);
      return getAxiosErrorReturnResults(err);
    }
  },

  createProject: async (apiBaseUrl, getAccessToken, project) => {
    try {
      const accessToken = await getAccessToken();
      if (accessToken === null) {
        set((state) => ({ loading: true }));
        return { succeeded: false, errorMessage: "Unable to retrieve authorized access token." };
      }

      const response = await axios.post(
        `${apiBaseUrl}/api/project/create`,
        {
          ProjectName: project.projectName,
          ImageData: project.projectImage,
          ImageType: project.contentType,
        },
        {
             headers: { Authorization: `Bearer ${accessToken}` },
        },
      );
      set((state) => ({
        isDirty: false,
        projectID: response.data.projectID,
        projectName: project.projectName,
        isOwned: true,
        plotPlanImage: project.projectImage,
        imageFileType: project.contentType,
        scalingFactor: null,
        distanceID: 0,
        version: null,
        updatedDate: null,
        networkContainers: [],
        activeNetwork: null,
        activeNetworkID: 0,
        rowVersion: null,
        loading: false,
        projectChanges: createCleanProjectChanges(),
        obstructionInstances: [],
      }));
      return { succeeded: true, errorMessage: null };
    } catch (err) {
      console.log("createProject threw an error", err);
      return getAxiosErrorReturnResults(err);
    }
  },

  saveProject: async (apiBaseUrl, getAccessToken) => {
    try {
      // Create a list of the obstruction instances that have changed
      let changedInstances: IUpdateObstructionInstance[] = [];
      if (get().obstructionInstances?.length > 0) {
        changedInstances = get()
          .obstructionInstances.filter(
            (item) =>
              item.obstructionInstanceID < 0 ||
              get().modifiedObstructionInstances.find(
                (ch) => ch.action === "update" && ch.obstructionInstanceID === item.obstructionInstanceID,
              ),
          )
          .map((item) =>
            item.obstructionInstanceID < 0
              ? { ...item, obstructionInstanceID: undefined, rowVersion: undefined, deleted: undefined }
              : { ...item, deleted: undefined },
          );
      }
      if (get().modifiedObstructionInstances?.length > 0) {
        get().modifiedObstructionInstances.forEach((ch) => {
          if (ch.action === "delete") {
            changedInstances.push(createDeletedObstructionInstance(ch.obstructionInstanceID, ch.rowVersion!));
          }
        });
      }

      // Create a list of the network containers that have changed
      let changedContainers: IUpdateNetworkContainer[] = [];
      if (get().networkContainers?.length > 0) {
        changedContainers = get()
          .networkContainers.filter(
            (item) =>
              item.networkContainerID < 0 ||
              get().modifiedNetworkContainers.find(
                (ch) => ch.action === "update" && ch.networkContainerID === item.networkContainerID,
              ),
          )
          .map((item) =>
            item.networkContainerID < 0
              ? {
                  networkContainerID: item.networkContainerID,
                  networkContainerName: item.networkContainerName,
                  gatewayDeviceInstance: item.gatewayDeviceInstance,
                  rowVersion: undefined,
                  deleted: undefined,
                }
              : {
                  networkContainerID: item.networkContainerID,
                  networkContainerName: item.networkContainerName,
                  gatewayDeviceInstance: item.gatewayDeviceInstance,
                  rowVersion: item.rowVersion,
                  deleted: undefined,
                },
          );
      }
      if (get().modifiedNetworkContainers?.length > 0) {
        get().modifiedNetworkContainers.forEach((ch) => {
          if (ch.action === "delete") {
            changedContainers.push(createDeletedNetworkContainer(ch.networkContainerID, ch.rowVersion!));
          }
        });
      }

      set((state) => ({
        projectChanges: {
          ...state.projectChanges,
          ProjectID: state.projectID,
          RowVersion: state.rowVersion,
          ScalingFactor: state.scalingFactor,
          DistanceID: state.distanceID,
          UpdateObstructionInstanceReqs: changedInstances,
          UpdateNetworkContainerReqs: changedContainers,
        },
        loading: true,
      }));
      const request = get().projectChanges;

      const accessToken = await getAccessToken();
      if (accessToken === null) {
        set((state) => ({ loading: true }));
        return { succeeded: false, errorMessage: "Unable to retrieve authorized access token." };
      }

      await axios.post(`${apiBaseUrl}/api/project/save`, request, {
          headers: { Authorization: `Bearer ${accessToken}` },
      });
      set(() => ({
        loading: false,
        isDirty: false,
        projectChanges: createCleanProjectChanges(),
      }));
      return { succeeded: true, errorMessage: null };
    } catch (err) {
      console.log("saveProject was unsuccessful", err);
      set(() => ({ loading: false }));
      return getAxiosErrorReturnResults(err);
    }
  },

  setProjectID: (n) => set(() => ({ projectID: n })),

  setName: (n) => set(() => ({ projectName: n, isDirty: true })),

  setPlotPlanImage: (image, filetype) => set(() => ({ plotPlanImage: image, imageFileType: filetype, isDirty: true })),

  setScalingFactor: (factor) => set(() => ({ scalingFactor: factor, isDirty: true })),

  setDistanceID: (distanceID) => set(() => ({ distanceID: distanceID, isDirty: true })),

  addNetwork: (networkContainerID) => {
    const networkID = get().nextNewNetworkID;
    set((state) => ({ nextNewNetworkID: state.nextNewNetworkID - 1 }));
    var net: IAddNetwork = {
      NetworkID: networkID,
      NetworkContainerID: networkContainerID,
      AddDeviceInstanceReqs: null,
    };
    const newNetwork: INetwork = {
      networkID: networkID,
      networkContainerID: networkContainerID,
      deviceInstances: [],
      updatedDate: new Date(),
      rowVersion: null,
      metaData: {
        usagePercent: 0,
        isUsagePercentDirty: false, // No devices in new network so usage percent = 0 is accurate
        isVisible: false,
      },
    };

    let replacementContainer: INetworkContainer | null = null;

    set((state) => ({
      isDirty: true,
      networkContainers: state.networkContainers.map((con) => {
        if (con.networkContainerID === networkContainerID) {
          replacementContainer = {
            ...con,
            networks: [...con.networks, newNetwork],
          };
          return replacementContainer;
        }
        return con;
      }),
      projectChanges: {
        ...state.projectChanges,
        AddNetworkReqs: [...(state.projectChanges.AddNetworkReqs || []), net],
      },
    }));

    if (networkContainerID === get().activeNetworkContainerID) {
      set(() => ({ activeNetworkContainer: replacementContainer }));
      get().setActiveNetwork(networkID);
    }

    return networkID;
  },

  //deleteNetworks : delete any devices in projectChanges that are associated with the network, remove from add/update arrays if applicable
  deleteNetwork: (networkID) => {
    const deletedNetwork = getNetwork(networkID, get().networkContainers);

    // Should we be returning an error for this?
    if (deletedNetwork === undefined) {
      console.log(`ERROR: deleteNetwork called with invalid ID: ${networkID}`);
      return;
    }

    //Check if addNetworkReq (remove from changes)
    if (networkID < 0) {
      const addReqs = get().projectChanges.AddNetworkReqs?.filter((net) => net.NetworkID !== networkID);
      set((state) => ({
        projectChanges: {
          ...state.projectChanges,
          AddNetworkReqs: addReqs && addReqs?.length > 0 ? addReqs : null,
        },
      }));
    } else {
      //Check if in addDeviceReq (remove from changes)
      if (get().projectChanges.AddDeviceInstanceReqs?.some((dev) => dev.NetworkID === networkID)) {
        const addReqs = get().projectChanges.AddDeviceInstanceReqs?.filter((dev) => dev.NetworkID !== networkID);
        set((state) => ({
          projectChanges: {
            ...state.projectChanges,
            AddDeviceInstanceReqs: addReqs!.length > 0 ? addReqs! : null,
          },
        }));
      }

      //Check if in updateDeviceReq (remove from changes)
      if (get().projectChanges.UpdateDeviceInstanceReqs?.some((dev) => dev.NetworkID === networkID)) {
        const updateReqs = get().projectChanges.UpdateDeviceInstanceReqs?.filter((dev) => dev.NetworkID !== networkID);
        set((state) => ({
          projectChanges: {
            ...state.projectChanges,
            UpdateDeviceInstanceReqs: updateReqs!.length > 0 ? updateReqs! : null,
          },
        }));
      }

      //Check if in deleteDeviceReq (remove from changes)
      if (get().projectChanges.DeleteDeviceInstanceReqs?.some((dev) => dev.NetworkID === networkID)) {
        const deleteReqs = get().projectChanges.DeleteDeviceInstanceReqs?.filter((dev) => dev.NetworkID !== networkID);
        set((state) => ({
          projectChanges: {
            ...state.projectChanges,
            DeleteDeviceInstanceReqs: deleteReqs!.length > 0 ? deleteReqs! : null,
          },
        }));
      }
    }

    // Add to deleteNetworkReqs
    const newDeleteReq: IDeleteNetwork = { NetworkID: networkID, RowVersion: deletedNetwork.rowVersion! };
    set((state) => ({
      projectChanges: {
        ...state.projectChanges,
        DeleteNetworkReqs:
          state.projectChanges.DeleteNetworkReqs !== null
            ? [...state.projectChanges.DeleteNetworkReqs, newDeleteReq]
            : [newDeleteReq],
      },
    }));

    const revisedContainers = get().networkContainers.map((con) =>
      con.networkContainerID === deletedNetwork.networkContainerID
        ? {
            ...con,
            networks: con.networks.filter((net) => net.networkID !== networkID),
          }
        : con,
    );

    set((state) => ({
      isDirty: true,
      networkContainers: revisedContainers,
      activeNetworkContainer: revisedContainers.find(
        (con) => con.networkContainerID === deletedNetwork.networkContainerID,
      ),
    }));

    // If deleted network leaves container empty, need to add a new network. Otherwise if deleted network was active, need to set new active network
    const container = get().networkContainers.find(
      (con) => con.networkContainerID === deletedNetwork.networkContainerID,
    )!;
    if (container.networks.length === 0) {
      get().addNetwork(container.networkContainerID);
    } else if (get().activeNetworkID === deletedNetwork.networkID) {
      get().setActiveNetwork(container.networks[0].networkID);
    }
  },

  setActiveNetwork: (networkID) => {
    let network = get().activeNetworkContainer?.networks.find((net) => net.networkID === networkID);
    if (network === undefined) {
      // Now need to try looking in the other containers
      for (const container of get().networkContainers) {
        network = container.networks.find((net) => net.networkID === networkID);
        if (network !== undefined) {
          break;
        }
      }
      if (network === undefined) {
        console.log(`ERROR: setActiveNetwork couldn't find network with ID = ${networkID}`);
        console.trace();
        return;
      }
    }

    const newActiveNetwork: INetwork = {
      ...network,
      metaData: {
        ...network.metaData,
        isVisible: true,
      },
    };

    set((state) => ({
      activeNetworkID: networkID,
      activeNetwork: newActiveNetwork,
      networkContainers: state.networkContainers.map((con) =>
        con.networkContainerID === newActiveNetwork.networkContainerID
          ? {
              ...con,
              networks: con.networks.map((net) => (net.networkID === networkID ? newActiveNetwork : net)),
            }
          : con,
      ),
    }));

    get().setActiveNetworkContainer(newActiveNetwork.networkContainerID, false);
  },

  setActiveNetworkContainer: (networkContainerID, setDefaultNetwork) => {
    const container = get().networkContainers.find((con) => con.networkContainerID === networkContainerID);
    if (container === undefined) {
      console.log(`ERROR: setActiveNetworkContainer couldn't find network with ID = ${networkContainerID}`);
      console.trace();
      return;
    }

    const newActiveNetworkContainer: INetworkContainer = {
      ...container,
      networks: container.networks.map((net) => {
        return {
          ...net,
          metaData: {
            ...net.metaData,
            isVisible: true,
          },
        };
      }),
      metaData: {
        ...container.metaData,
        isVisible: true,
      },
    };

    set((state) => ({
      activeNetworkContainerID: networkContainerID,
      activeNetworkContainer: newActiveNetworkContainer,
      activeGateway: newActiveNetworkContainer.gatewayDeviceInstance,
      networkContainers: state.networkContainers.map((con) => {
        if (con.networkContainerID === networkContainerID) {
          return newActiveNetworkContainer;
          // Note: It is possible that the new active is the same as existing but the initial conditional statement will take
          // presidence and the visibility will be set to true
        } else if (con.networkContainerID === state.activeNetworkContainerID) {
          return {
            ...con,
            networks: con.networks.map((net) => {
              return {
                ...net,
                metaData: {
                  ...net.metaData,
                  isVisible: false,
                },
              };
            }),
            metaData: {
              ...con.metaData,
              isVisible: false,
            },
          };
        }
        return con;
      }),
    }));

    if (setDefaultNetwork && container.networks.length > 0) {
      get().setActiveNetwork(container.networks[0].networkID);
    }
  },

  addNetworkContainer: (name) => {
    const priorNetworkContainerCount = get().networkContainers.length;
    const networkContainerID = get().nextNewNetworkContainerID;
    set((state) => ({ nextNewNetworkContainerID: state.nextNewNetworkContainerID - 1 }));

    const newNetworkContainer: INetworkContainer = {
      networkContainerID: networkContainerID,
      networkContainerName: name,
      gatewayDeviceInstance: null,
      updatedDate: new Date(),
      rowVersion: null,
      networks: [],
      metaData: {
        isVisible: false,
      },
    };
    set((state) => ({
      isDirty: true,
      networkContainers: [...state.networkContainers, newNetworkContainer],
    }));
    // need to add a default network to the network container
    get().addNetwork(networkContainerID);

    if (priorNetworkContainerCount === 0) {
      get().setActiveNetworkContainer(networkContainerID, true);
    }

    return networkContainerID;
  },

  // used to update network name
  updateNetworkContainer: (networkContainerID, newName) => {
    set((state) => ({
      isDirty: true,
      networkContainers: state.networkContainers.map((container) => {
        if (container.networkContainerID === networkContainerID) {
          return {
            ...container,
            networkContainerName: newName,
          };
        }
        return container;
      }),
    }));
    // If not found in modified collection, add it
    if (
      get().modifiedNetworkContainers.find((container) => container.networkContainerID === networkContainerID) ===
      undefined
    ) {
      set((state) => ({
        modifiedNetworkContainers: [
          ...state.modifiedNetworkContainers,
          {
            networkContainerID: networkContainerID,
            rowVersion: null,
            action: "update",
          },
        ],
      }));
    }
  },

  deleteNetworkContainer: (networkContainerID) => {
    const deletedNetworkContainer = get().networkContainers.find(
      (networkContainer) => networkContainer.networkContainerID === networkContainerID,
    );

    // Should we be returning an error for this?
    if (deletedNetworkContainer === undefined) {
      console.log(`ERROR: deleteNetworkContainer called with invalid ID: ${networkContainerID}`);
      return;
    }

    // If no networks remain, need to add the default one
    const revisedList = get().networkContainers.filter(
      (networkContainer) => networkContainer.networkContainerID !== networkContainerID,
    );
    const revisedActiveIndex = Math.max(
      0,
      get().networkContainers.findIndex(
        (networkContainer) => networkContainer.networkContainerID === networkContainerID,
      ) - 1,
    );
    const currentCount = get().networkContainers.length;
    set((state) => ({
      isDirty: true,
      networkContainers: state.networkContainers.filter(
        (networkContainer) => networkContainer.networkContainerID !== networkContainerID,
      ),
      activeNetworkContainerID: currentCount === 1 ? 0 : revisedList[revisedActiveIndex].networkContainerID,
      activeNetworkContainer: currentCount === 1 ? null : revisedList[revisedActiveIndex],
    }));
    if (currentCount === 1) {
      get().addNetworkContainer("Default Network");
    }

    // Need to remove any networks that are in the container from the projectChanges
    if (deletedNetworkContainer.networkContainerID < 0) {
      set((state) => ({
        projectChanges: {
          ...state.projectChanges,
          AddNetworkReqs:
            state.projectChanges.AddNetworkReqs?.filter(
              (net) => net.NetworkID !== deletedNetworkContainer.networkContainerID,
            ) || null,
        },
      }));
    } else {
      // Otherwise update modified collection
      set((state) => ({
        modifiedNetworkContainers: [
          ...state.modifiedNetworkContainers.filter((con) => con.networkContainerID !== networkContainerID),
          {
            networkContainerID: networkContainerID,
            rowVersion: deletedNetworkContainer.rowVersion,
            action: "delete",
          },
        ],
      }));
    }
  },

  setGateway: (newGateway) => {
    const deviceInstanceID = get().nextNewDeviceInstanceID;
    set((state) => ({ nextNewDeviceInstanceID: state.nextNewDeviceInstanceID - 1 }));

    const replacementGateway = {
      ...newGateway,
      deviceInstanceID: deviceInstanceID,
    };

    const replacementContainer = {
      ...get().activeNetworkContainer!,
      gatewayDeviceInstance: replacementGateway,
    };

    set((state) => ({
      isDirty: true,
      networkContainers: state.networkContainers.map((container) => {
        if (container.networkContainerID === state.activeNetworkContainerID) {
          return replacementContainer;
        }
        return container;
      }),
      activeGateway: replacementGateway,
      activeNetworkContainer: replacementContainer,
    }));

    // Also need to update modified collection
    if (
      get().modifiedNetworkContainers.find((con) => con.networkContainerID === get().activeNetworkContainerID) ===
      undefined
    ) {
      set((state) => ({
        modifiedNetworkContainers: [
          ...state.modifiedNetworkContainers,
          {
            networkContainerID: state.activeNetworkContainerID,
            rowVersion: state.activeNetworkContainer!.rowVersion,
            action: "update",
          },
        ],
      }));
    }

    return deviceInstanceID;
  },

  updateGateway: (networkContainerID, gatewayInstance) => {
    const replacementGateway = {
      ...gatewayInstance,
    };

    let replacementContainer: INetworkContainer | null = null;

    set((state) => ({
      isDirty: true,
      networkContainers: state.networkContainers.map((container) => {
        if (container.networkContainerID === networkContainerID) {
          replacementContainer = {
            ...container,
            gatewayDeviceInstance: replacementGateway,
          };
          return replacementContainer;
        }
        return container;
      }),
    }));

    if (networkContainerID === get().activeNetworkContainerID) {
      set(() => ({ activeNetworkContainer: replacementContainer, activeGateway: replacementGateway }));
    }

    // Also need to update modified collection
    if (
      get().modifiedNetworkContainers.find((con) => con.networkContainerID === get().activeNetworkContainerID) ===
      undefined
    ) {
      set((state) => ({
        modifiedNetworkContainers: [
          ...state.modifiedNetworkContainers,
          {
            networkContainerID: state.activeNetworkContainerID,
            rowVersion: state.activeNetworkContainer!.rowVersion,
            action: "update",
          },
        ],
      }));
    }
  },

  addDeviceInstance: (newDevice, networkID) => {
    const deviceInstanceID = get().nextNewDeviceInstanceID;
    set((state) => ({ nextNewDeviceInstanceID: state.nextNewDeviceInstanceID - 1 }));

    newDevice.deviceInstanceID = deviceInstanceID;
    let replacementNetwork: INetwork;
    let replacementContainer: INetworkContainer | undefined = undefined;
    var oldContainer = get().networkContainers.flatMap((container) => container.networks).find(net => net.networkID === networkID);
    set((state) => ({
      isDirty: true,
      networkContainers: state.networkContainers.map((container) => {
        if (container.networkContainerID === oldContainer!.networkContainerID) {
          replacementContainer = {
            ...container,
            networks: container.networks.map((network) => {
              if (network.networkID === networkID) {
                replacementNetwork = {
                  ...network,
                  deviceInstances: [...network.deviceInstances, { ...newDevice }],
                  metaData: {
                    ...network.metaData,
                    isUsagePercentDirty: true,
                  },
                };
                return replacementNetwork;
              }
              return network;
            }),
          };
          return replacementContainer;
        }
        return container;
      }),
    }));

    if (
      replacementContainer &&
      get().activeNetworkContainerID === (replacementContainer as INetworkContainer).networkContainerID
    ) {
      set(() => ({ activeNetworkContainer: replacementContainer }));
    }
    if (get().activeNetworkID === networkID) {
      set(() => ({
        activeNetwork: replacementNetwork,
      }));
    }

    let dev: IAddDevice = {
      NetworkID: networkID,
      DeviceInstanceID: deviceInstanceID,
      DeviceID: newDevice.deviceID,
      DeviceTag: newDevice.deviceTag,
      XPosition: newDevice.xPosition,
      YPosition: newDevice.yPosition,
      Height: newDevice.height,
      Range: newDevice.range,
      UpdateRateID: newDevice.updateRateID,
      Note: newDevice.note,
    };

    if (networkID < 0) {
      //add dev to AddNetworkReqs
      set((state) => ({
        projectChanges: {
          ...state.projectChanges,
          AddNetworkReqs:
            state.projectChanges.AddNetworkReqs?.map((net) =>
              net.NetworkID === networkID
                ? {
                    ...net,
                    AddDeviceInstanceReqs: [
                      ...(state.projectChanges.AddNetworkReqs?.find((net2) => net2.NetworkID === networkID)
                        ?.AddDeviceInstanceReqs || []),
                      dev,
                    ],
                  }
                : net,
            ) || null,
        },
      }));
    } else {
      //add dev to AddDeviceReqs
      set((state) => ({
        projectChanges: {
          ...state.projectChanges,
          AddDeviceInstanceReqs: [...(state.projectChanges.AddDeviceInstanceReqs || []), dev],
        },
      }));
    }

    return deviceInstanceID;
  },

  updateDeviceInstances: (networkID, updateDevice) => {
    // First modify projectChanges
    //Check if addNetworkReq (modify record there)
    if (networkID < 0) {
      set((state) => ({
        projectChanges: {
          ...state.projectChanges,
          AddNetworkReqs:
            state.projectChanges.AddNetworkReqs?.map((net) =>
              net.NetworkID === networkID
                ? {
                    ...net,
                    AddDeviceInstanceReqs:
                      state.projectChanges.AddNetworkReqs?.find(
                        (net2) => net2.NetworkID === networkID,
                      )?.AddDeviceInstanceReqs?.map((dev) =>
                        dev.DeviceInstanceID === updateDevice.deviceInstanceID
                          ? {
                              ...dev,
                              DeviceTag: updateDevice.deviceTag,
                              XPosition: updateDevice.xPosition,
                              YPosition: updateDevice.yPosition,
                              Height: updateDevice.height,
                              Range: updateDevice.range,
                              UpdateRateID: updateDevice.updateRateID,
                              Note: updateDevice.note,
                            }
                          : dev,
                      ) || null,
                  }
                : net,
            ) || null,
        },
      }));
    } else if (updateDevice.deviceInstanceID < 0) {
      //Check if in addDeviceReq (modify record there)
      set((state) => ({
        projectChanges: {
          ...state.projectChanges,
          AddDeviceInstanceReqs:
            state.projectChanges.AddDeviceInstanceReqs?.map((dev) =>
              dev.DeviceInstanceID === updateDevice.deviceInstanceID
                ? {
                    ...dev,
                    DeviceTag: updateDevice.deviceTag,
                    XPosition: updateDevice.xPosition,
                    YPosition: updateDevice.yPosition,
                    Height: updateDevice.height,
                    Range: updateDevice.range,
                    UpdateRateID: updateDevice.updateRateID,
                    Note: updateDevice.note,
                  }
                : dev,
            ) || null,
        },
      }));
    } else if (
      get().projectChanges.UpdateDeviceInstanceReqs?.some(
        (dev) => dev.DeviceInstanceID === updateDevice.deviceInstanceID,
      )
    ) {
      //Check if in updateDeviceReq (modify record there)
      set((state) => ({
        projectChanges: {
          ...state.projectChanges,
          UpdateDeviceInstanceReqs:
            state.projectChanges.UpdateDeviceInstanceReqs?.map((dev) =>
              dev.DeviceInstanceID === updateDevice.deviceInstanceID
                ? {
                    ...dev,
                    DeviceTag: updateDevice.deviceTag,
                    XPosition: updateDevice.xPosition,
                    YPosition: updateDevice.yPosition,
                    Height: updateDevice.height,
                    Range: updateDevice.range,
                    UpdateRateID: updateDevice.updateRateID,
                    Note: updateDevice.note,
                  }
                : dev,
            ) || null,
        },
      }));
    } else {
      //else add to updateDeviceReq (add record)
      const deviceInstance = getNetwork(networkID, get().networkContainers)?.deviceInstances.find(
        (inst) => inst.deviceInstanceID === updateDevice.deviceInstanceID,
      );
      if (deviceInstance !== undefined) {
        const dev: IUpdateDevice = {
          DeviceInstanceID: updateDevice.deviceInstanceID,
          NetworkID: networkID,
          DeviceTag: updateDevice.deviceTag,
          XPosition: updateDevice.xPosition,
          YPosition: updateDevice.yPosition,
          Height: updateDevice.height,
          Range: updateDevice.range,
          UpdateRateID: updateDevice.updateRateID,
          Note: updateDevice.note,
          RowVersion: deviceInstance.rowVersion!,
        };
        set((state) => ({
          projectChanges: {
            ...state.projectChanges,
            UpdateDeviceInstanceReqs: [...(state.projectChanges.UpdateDeviceInstanceReqs || []), dev],
          },
        }));
      } else {
        console.log(
          `ERROR: Didn't find device instance with networkID = ${networkID} and instanceID = ${updateDevice.deviceInstanceID}`,
        );
      }
    }
    // projectChanges changes made
    // update networks
    let replacementNetwork: INetwork;
    let replacementContainer: INetworkContainer | undefined = undefined;
    set((state) => ({
      isDirty: true,
      networkContainers: state.networkContainers.map((container) => {
        if (container.networkContainerID === state.activeNetworkContainerID) {
          replacementContainer = {
            ...container,
            networks: container.networks.map((network) => {
              if (network.networkID === networkID) {
                let rateChanged: boolean = false;
                const deviceInstances = network.deviceInstances.map((inst) => {
                  if (inst.deviceInstanceID === updateDevice.deviceInstanceID) {
                    rateChanged = inst.updateRateID !== updateDevice.updateRateID;
                    return updateDevice;
                  }
                  return inst;
                });
                replacementNetwork = {
                  ...network,
                  deviceInstances: deviceInstances,
                  metaData: {
                    ...network.metaData,
                    isUsagePercentDirty: network.metaData.isUsagePercentDirty || rateChanged,
                  },
                };
                return replacementNetwork;
              }
              return network;
            }),
          };
          return replacementContainer;
        }
        return container;
      }),
    }));

    if (
      replacementContainer &&
      get().activeNetworkContainerID === (replacementContainer as INetworkContainer).networkContainerID
    ) {
      set(() => ({ activeNetworkContainer: replacementContainer }));
    }
    if (get().activeNetworkID === networkID) {
      set(() => ({
        activeNetwork: replacementNetwork,
      }));
    }
  },

  deleteDeviceInstance: (instanceID, networkID) => {
    // First modify project changes
    //Check if addNetworkReq (remove, no further action)
    if (networkID < 0) {
      set((state) => ({
        projectChanges: {
          ...state.projectChanges,
          AddNetworkReqs:
            state.projectChanges.AddNetworkReqs?.map((network) => {
              if (network.NetworkID === networkID) {
                const deviceInstances = network.AddDeviceInstanceReqs?.filter(
                  (inst) => inst.DeviceInstanceID !== instanceID,
                );
                return {
                  ...network,
                  AddDeviceInstanceReqs: deviceInstances || null,
                };
              }
              return network;
            }) || null,
        },
      }));
    } else if (instanceID < 0) {
      //Check if in addDeviceReq (remove, no further action)
      const copyReqs = get().projectChanges.AddDeviceInstanceReqs!.filter(
        (inst) => !(inst.DeviceInstanceID === instanceID && inst.NetworkID === networkID),
      );
      set((state) => ({
        projectChanges: {
          ...state.projectChanges,
          AddDeviceInstanceReqs: copyReqs,
        },
      }));
    } else {
      //else add to deleteDeviceReq
      //Check if in updateDeviceReq (remove, also add to deleteDeviceReq)
      if (get().projectChanges.UpdateDeviceInstanceReqs?.some((dev) => dev.DeviceInstanceID === instanceID)) {
        const copyReqs = get().projectChanges.UpdateDeviceInstanceReqs!.filter(
          (inst) => !(inst.DeviceInstanceID === instanceID && inst.NetworkID === networkID),
        );
        set((state) => ({
          projectChanges: {
            ...state.projectChanges,
            UpdateDeviceInstanceReqs: copyReqs,
          },
        }));
      }

      const deviceInstance = getNetwork(networkID, get().networkContainers)!.deviceInstances.find(
        (inst) => inst.deviceInstanceID === instanceID,
      );
      if (deviceInstance !== undefined) {
        let dev: IDeleteDevice = {
          DeviceInstanceID: instanceID,
          NetworkID: networkID,
          RowVersion: deviceInstance.rowVersion!,
        };
        set((state) => ({
          projectChanges: {
            ...state.projectChanges,
            DeleteDeviceInstanceReqs: [...(state.projectChanges.DeleteDeviceInstanceReqs || []), dev],
          },
        }));
      } else {
        console.log(
          `ERROR: unable to delete device instance with networkID = ${networkID} and instanceID = ${instanceID}`,
          console.trace(),
        );
      }
    }
    // projectChanges changes finished
    // Remove from deviceInstance array
    let replacementNetwork: INetwork;
    let replacementContainer: INetworkContainer | undefined = undefined;
    set((state) => ({
      isDirty: true,
      networkContainers: state.networkContainers.map((container) => {
        if (container.networkContainerID === state.activeNetworkContainerID) {
          replacementContainer = {
            ...container,
            networks: container.networks.map((network) => {
              if (network.networkID === networkID) {
                replacementNetwork = {
                  ...network,
                  deviceInstances: network.deviceInstances.filter((inst) => inst.deviceInstanceID !== instanceID),
                  metaData: {
                    ...network.metaData,
                    isUsagePercentDirty: true,
                  },
                };
                return replacementNetwork;
              }
              return network;
            }),
          };
          return replacementContainer;
        }
        return container;
      }),
    }));

    if (
      replacementContainer &&
      get().activeNetworkContainerID === (replacementContainer as INetworkContainer).networkContainerID
    ) {
      set(() => ({ activeNetworkContainer: replacementContainer }));
    }
    if (get().activeNetworkID === networkID) {
      set(() => ({
        activeNetwork: replacementNetwork,
      }));
    }
  },

  recalculateNetworkUsage: async (apiBaseUrl, networkID) => {
    try {
      // Ignore if can't find network
      const network = getNetwork(networkID, get().networkContainers);
      if (network === undefined) {
        return { succeeded: false, errorMessage: "Invalid network ID" };
      }

      const deviceUpdateRates = network.deviceInstances
        .filter((di) => di.updateRateID !== null)
        .map((di) => ({
          deviceID: di.deviceID,
          updateRateID: di.updateRateID,
        }));
      const response = await axios.post(`${apiBaseUrl}/api/network/calculateusagepercent`, {
        deviceUpdateRates: deviceUpdateRates,
      });

      let replacementNetwork: INetwork;
      let replacementContainer: INetworkContainer | undefined = undefined;
      set((state) => ({
        networkContainers: state.networkContainers.map((container) => {
          if (container.networkContainerID === network.networkContainerID) {
            replacementContainer = {
              ...container,
              networks: container.networks.map((net) => {
                if (net.networkID === networkID) {
                  replacementNetwork = {
                    ...net,
                    metaData: {
                      usagePercent: parseInt(response.data.usagePercent),
                      isUsagePercentDirty: false,
                      isVisible: net.metaData.isVisible,
                    },
                  };
                  return replacementNetwork;
                } else {
                  return net;
                }
              }),
            };
            return replacementContainer;
          } else {
            return container;
          }
        }),
      }));

      if (
        replacementContainer &&
        get().activeNetworkContainerID === (replacementContainer as INetworkContainer).networkContainerID
      ) {
        set(() => ({ activeNetworkContainer: replacementContainer }));
      }
      if (get().activeNetworkID === networkID) {
        set(() => ({
          activeNetwork: replacementNetwork,
        }));
      }
      return { succeeded: true, errorMessage: null };
    } catch (err) {
      console.log("recalculateNetworkUsage threw an error", err);
      const msg = getAxiosErrorReturnResults(err);
      console.log(msg);
      return getAxiosErrorReturnResults(err);
    }
  },

  setNetworkVisiblity: (networkID, isVisible) => {
    if (networkID === get().activeNetworkID) {
      return;
    }

    let replacementNetwork: INetwork;
    let replacementContainer: INetworkContainer | undefined = undefined;

    var network = getNetwork(networkID, get().networkContainers);
    if (network === undefined) {
      return;
    }

    set((state) => ({
      networkContainers: state.networkContainers.map((con) => {
        if (con.networkContainerID === network!.networkContainerID) {
          replacementContainer = {
            ...con,
            metaData: {
              isVisible:
                con.networks.length === 2
                  ? con.networks[0].networkID === networkID
                    ? isVisible || con.networks[1].metaData.isVisible
                    : con.networks[0].metaData.isVisible || isVisible
                  : isVisible,
            },
            networks: con.networks.map((net) => {
              if (net.networkID === networkID) {
                replacementNetwork = {
                  ...net,
                  metaData: {
                    ...net.metaData,
                    isVisible: isVisible,
                  },
                };
                return replacementNetwork;
              } else {
                return net;
              }
            }),
          };
          return replacementContainer;
        } else {
          return con;
        }
      }),
    }));

    if (
      replacementContainer &&
      get().activeNetworkContainerID === (replacementContainer as INetworkContainer).networkContainerID
    ) {
      set(() => ({ activeNetworkContainer: replacementContainer }));
    }
    if (get().activeNetworkID === networkID) {
      set(() => ({
        activeNetwork: replacementNetwork,
      }));
    }
  },

  addObstructionInstance: (newInstance) => {
    const obstructionInstanceID = get().nextNewObstructionInstanceID;
    set((state) => ({ nextNewObstructionInstanceID: state.nextNewObstructionInstanceID - 1 }));

    newInstance.obstructionInstanceID = obstructionInstanceID;
    set((state) => ({
      isDirty: true,
      obstructionInstances: [...state.obstructionInstances, { ...newInstance }],
    }));
  },

  updateObstructionInstance: (obstructionID, updateInstance) => {
    set((state) => ({
      isDirty: true,
      obstructionInstances: state.obstructionInstances.map((item) =>
        item.obstructionInstanceID === obstructionID
          ? { ...updateInstance, obstructionInstanceID: item.obstructionInstanceID, rowVersion: item.rowVersion }
          : item,
      ),
    }));
    if (get().modifiedObstructionInstances.find((item) => item.obstructionInstanceID === obstructionID) === undefined) {
      set((state) => ({
        modifiedObstructionInstances: [
          ...state.modifiedObstructionInstances,
          {
            obstructionInstanceID: obstructionID,
            rowVersion: null,
            action: "update",
          },
        ],
      }));
    }
  },

  deleteObstructionInstance: (obstructionID) => {
    const existingInstance = get().obstructionInstances.find((item) => item.obstructionInstanceID === obstructionID);
    if (existingInstance !== undefined) {
      set((state) => ({
        isDirty: true,
        obstructionInstances: state.obstructionInstances.filter((item) => item.obstructionInstanceID !== obstructionID),
      }));
      if (existingInstance.obstructionInstanceID > 0) {
        set((state) => ({
          modifiedObstructionInstances: [
            ...state.modifiedObstructionInstances,
            {
              obstructionInstanceID: obstructionID,
              rowVersion: existingInstance.rowVersion,
              action: "delete",
            },
          ],
        }));
      }
    } else {
      console.log(`tried to delete non existing ObstructionInstance with ID - ${obstructionID}`);
    }
  },

  projectChanges: {
    ProjectID: null,
    RowVersion: null,
    ScalingFactor: null,
    DistanceID: null,
    AddNetworkReqs: null,
    UpdateNetworkReqs: null,
    DeleteNetworkReqs: null,
    AddDeviceInstanceReqs: null,
    UpdateDeviceInstanceReqs: null,
    DeleteDeviceInstanceReqs: null,
    UpdateObstructionInstanceReqs: null,
    UpdateNetworkContainerReqs: null,
  },
  loading: false,
  zoomPercent: 100,
  setZoomPercent: (percent) => set(() => ({ zoomPercent: percent })),
  transparencyPercent: 60,
  setTransparencyPercent: (percent) =>
    set(() => ({
      transparencyPercent: percent,
    })),
  iconPercent: 100,
  setIconPercent: (percent) => set(() => ({ iconPercent: percent })),
  enableStressTest: 100,
  setEnableStressTest: (value) => set(() => ({ enableStressTest: value })),
  resetProject: () => {
    //reset project to initial state (other variables get overwritten on create project)
    set(() => ({
      scalingFactor: null,
      networkContainers: [],
      obstructionInstances: [],
      distanceID: DistanceUnit.Feet,
    }));
  },
}));

export default useProjectStore;
